import InfiniteScroll from 'react-infinite-scroll-component';
import { Role } from 'auth';
import { useCallback, useContext, useEffect, useState } from 'react';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import UserManagementItem from '@/modules/admin-dashboard/components/items/UserManagementItem.tsx';
import { AddUserModal } from '@/modules/admin-dashboard/components/modals/AddUserModal.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { useUsersListQuery } from '@/modules/admin-dashboard/queries/users.ts';
import { Button, EmptyListState, ModalProviderContext, PageLayout, SearchField, Spinner } from 'ui';

const UserManagement = () => {
  const businessId = useGetCurrentBusinessId();
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setSearchValue('');
  }, [businessId]);
  const { openModal } = useContext(ModalProviderContext);
  const {
    hasNextPage,
    fetchNextPage,
    data: usersData,
    isLoading,
  } = useUsersListQuery({
    page: 1,
    page_size: 18,
    filter_by_name: searchValue,
  });

  const users = (usersData?.pages || []).flatMap(({ data }) => data);

  const handleOpenAddUserModal = useCallback(() => {
    openModal(AddUserModal);
  }, [openModal]);

  const headerAction = (
    <div className="flex flex-1 max-w-[400px] flex-wrap-reverse w-full justify-end items-center gap-3">
      <SearchField
        className="flex-1 !w-[240px]"
        placeholder="Search"
        value={searchValue}
        onChange={setSearchValue}
      />
      <Button onClick={handleOpenAddUserModal} variant="primary" className="flex-1 min-w-fit">
        Add new user
      </Button>
    </div>
  );

  return (
    <PageLayout title="User management" headerAction={headerAction}>
      {isLoading && (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="lg" variant="primary" />
        </div>
      )}
      <EmptyListState
        title="No users found"
        description="You can add users, add them into departments and assign tasks into entire departments. You can add users from here"
        isLoading={isLoading}
        isSearchUsed={!!searchValue}
        itemsCount={users.length}
      />
      {!!users.length && (
        <InfiniteScroll
          next={fetchNextPage}
          dataLength={users.length}
          hasMore={!!hasNextPage}
          className="w-full flex flex-col space-y-2"
          style={{ overflow: 'unset' }}
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          scrollableTarget="scrollable"
        >
          {users.map(user => (
            <UserManagementItem user={user} key={user.id} />
          ))}
        </InfiniteScroll>
      )}
    </PageLayout>
  );
};

export default withPrivateAccess(UserManagement, Role.COMPLIANCE_ADMIN);
